import { useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Avatar } from "@/components/ui/Avatar"
import { Button } from "@/components/ui/Button"
import { settings_profile_avatar_path } from "@/lib/routes"

const ACCEPT = "image/jpeg,image/png,image/gif,image/webp"

type Busy = "upload" | "remove" | null

export interface AvatarUser {
  name: string | null
  avatar_url: string | null
  /** "upload" (the person's own photo) or "slack" (synced); null without a photo. */
  avatar_source: string | null
}

// Profile photo row: the current photo (or initials), "Upload photo", and
// "Remove" for a photo the person uploaded. Picking a file uploads straight
// away — no separate Save. A Slack-synced photo has no Remove: the next
// login's sync would only bring it back (Users::SyncSlackAvatarWorker), so
// the way to change it is to upload a photo over it.
export default function AvatarField({ user }: { user: AvatarUser }) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<Busy>(null)
  const errors = usePage().props.errors as Record<string, string | string[] | undefined> | undefined
  const error = errors?.avatar
  const fromSlack = user.avatar_source === "slack"
  const removable = Boolean(user.avatar_url) && user.avatar_source === "upload"

  const visit = (kind: Exclude<Busy, null>) => ({
    preserveScroll: true,
    onStart: () => setBusy(kind),
    onFinish: () => setBusy(null),
  })

  const upload = (file: File | undefined) => {
    if (!file) return
    router.patch(settings_profile_avatar_path(), { avatar: file }, { ...visit("upload"), forceFormData: true })
  }

  const remove = () => {
    if (!window.confirm("Remove your photo? You'll show initials instead.")) return
    router.delete(settings_profile_avatar_path(), visit("remove"))
  }

  return (
    <div className="flex items-center gap-4">
      <Avatar size="xl" name={user.name ?? ""} avatarUrl={user.avatar_url} />
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              upload(e.target.files?.[0])
              e.target.value = ""
            }}
          />
          <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => input.current?.click()}>
            {busy === "upload" ? "Uploading…" : "Upload photo"}
          </Button>
          {removable && (
            <Button type="button" variant="ghost" size="sm" disabled={busy !== null} onClick={remove}>
              {busy === "remove" ? "Removing…" : "Remove"}
            </Button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {fromSlack ? "From your Slack profile. Upload a photo to replace it." : "JPG, PNG, GIF or WebP, up to 10 MB."}
        </span>
        {error && (
          <span className="text-xs text-destructive">{Array.isArray(error) ? error.join(", ") : error}</span>
        )}
      </div>
    </div>
  )
}
